/**
 * EventBus API client for managing event subscriptions
 */
import type { HttpClient } from '../utils/http';

/**
 * API response for subscription operations
 * @internal
 */
interface SubscriptionResponse {
  subscriptionId: string;
  status: string;
  statusDetails?: {
    lastError?: string;
    lastErrorAt?: string;
    retryCount?: number;
    nextRetryAt?: string;
  };
  eventStoreId: string;
  eventFilter: string;
  subscriberId: string;
  subscriberType: string;
  createdAt: string;
  updatedAt: string;
  lastProcessedEventGlobalPosition?: number;
  isExistingSubscription?: boolean;
  message?: string;
}

/**
 * Available subscriber types for event subscriptions
 */
export enum SubscriberType {
  DurableObject = 'durable_object',
  Webhook = 'webhook',
  Queue = 'queue',
  CloudflareWorkflow = 'cloudflare_workflow',
  WebSocket = 'websocket',
}

/**
 * Subscription status values
 */
export enum SubscriptionStatus {
  Active = 'ACTIVE',
  Suspended = 'SUSPENDED',
  Error = 'ERROR',
  Initializing = 'INITIALIZING',
  Deleted = 'DELETED',
}

/**
 * Retry policy configuration for subscribers
 */
export interface RetryPolicy {
  /**
   * Maximum number of delivery attempts before giving up
   */
  maxAttempts: number;

  /**
   * How many minutes to wait between retry attempts
   */
  backoffMinutes: number;
}

/**
 * Base configuration shared by all subscriber types
 */
export interface BaseSubscriberConfig {
  /**
   * Configuration for retry behavior
   */
  retryPolicy?: RetryPolicy;
}

/**
 * Configuration for Durable Object subscribers
 */
export interface DurableObjectConfig extends BaseSubscriberConfig {
  /**
   * The Durable Object namespace binding name in your worker
   */
  namespace: string;

  /**
   * The ID of the Durable Object instance to deliver events to
   */
  id: string;
}

/**
 * Configuration for webhook subscribers
 */
export interface WebhookConfig extends BaseSubscriberConfig {
  /**
   * The URL that will receive HTTP POST requests with events
   */
  url: string;

  /**
   * Custom headers to include in the webhook request
   */
  headers?: Record<string, string>;

  /**
   * Timeout in milliseconds for the webhook request
   */
  timeoutMs?: number;
}

/**
 * Configuration for queue subscribers
 */
export interface QueueConfig extends BaseSubscriberConfig {
  /**
   * The name of the queue to send events to
   */
  queueName: string;

  /**
   * The region of the queue
   */
  region?: string;

  /**
   * Maximum number of events to batch in a single queue message
   */
  batchSize?: number;
}

/**
 * Configuration for Cloudflare Workflow subscribers
 */
export interface CloudflareWorkflowConfig extends BaseSubscriberConfig {
  /**
   * The name of the workflow binding in your worker
   */
  bindingName: string;
}

/**
 * Configuration for WebSocket subscribers
 */
export interface WebSocketConfig extends BaseSubscriberConfig {
  /**
   * The ID of the WebSocket manager to broadcast events to
   */
  managerId: string;
}

/**
 * Union type for all subscriber configurations
 */
export type SubscriberConfig = {
  type: SubscriberType.Webhook;
  config: WebhookConfig;
};
// | {
//     type: SubscriberType.DurableObject;
//     config: DurableObjectConfig;
//   }
// | {
//     type: SubscriberType.Webhook;
//     config: WebhookConfig;
//   }
// | {
//     type: SubscriberType.Queue;
//     config: QueueConfig;
//   }
// | {
//     type: SubscriberType.CloudflareWorkflow;
//     config: CloudflareWorkflowConfig;
//   }
// | {
//     type: SubscriberType.WebSocket;
//     config: WebSocketConfig;
//   };

/**
 * Type of initial position for subscriptions
 */
export type InitialPosition = 'latest' | 'earliest';

/**
 * Event filter pattern for subscriptions
 */
export type EventFilterPattern = string | string[];

/**
 * Status details for a subscription
 */
export interface SubscriptionStatusDetails {
  /**
   * Last error message if the subscription is in an error state
   */
  lastError?: string;

  /**
   * When the last error occurred
   */
  lastErrorAt?: string;

  /**
   * Number of retries that have been attempted
   */
  retryCount?: number;

  /**
   * When the next retry will be attempted
   */
  nextRetryAt?: string;
}

/**
 * Complete subscription information
 */
export interface Subscription {
  /**
   * Unique identifier for this subscription
   */
  subscriptionId: string;

  /**
   * Current status of the subscription
   */
  status: SubscriptionStatus;

  /**
   * Additional details about the current status
   */
  statusDetails?: SubscriptionStatusDetails;

  /**
   * The event store this subscription is connected to
   */
  eventStoreId: string;

  /**
   * The event pattern this subscription is filtering on
   */
  eventFilter: string;

  /**
   * The ID of the subscriber
   */
  subscriberId: string;

  /**
   * Type of subscriber receiving events
   */
  subscriberType: SubscriberType;

  /**
   * When this subscription was created
   */
  createdAt: string;

  /**
   * When this subscription was last updated
   */
  updatedAt: string;

  /**
   * The global position of the last successfully delivered event
   */
  lastProcessedEventGlobalPosition?: number;

  /**
   * Whether this is an existing subscription that was found instead of creating a new one
   */
  isExistingSubscription?: boolean;

  /**
   * Human-readable message about the subscription operation
   */
  message?: string;
}

/**
 * Options for creating a subscription
 */
export interface SubscribeOptions {
  /**
   * Pattern(s) determining which events to receive
   */
  eventFilter: EventFilterPattern;

  /**
   * Optional custom ID for this subscription. If not provided, one will be generated.
   */
  subscriberId?: string;

  /**
   * Configuration for the subscriber that will receive events
   */
  subscriber: SubscriberConfig;

  /**
   * The position in the message stream from which a consumer starts consuming messages
   * when there is no prior checkpoint. Defaults to latest.
   */
  initialPosition?: InitialPosition;
}

/**
 * Options for listing subscriptions
 */
export interface ListSubscriptionsOptions {
  /**
   * Filter results by subscriber type
   */
  subscriberType?: SubscriberType;

  /**
   * Filter results by event filter pattern
   */
  eventFilter?: string;

  /**
   * Maximum number of results to return
   */
  limit?: number;

  /**
   * Number of results to skip
   */
  offset?: number;
}

/**
 * Response from listing subscriptions
 */
export interface ListSubscriptionsResponse {
  /**
   * Array of subscription details
   */
  subscriptions: Subscription[];

  /**
   * Total number of subscriptions matching the query
   */
  totalCount: number;
}

/**
 * Type for received events from a subscription
 */
export interface SubscriptionEvent {
  /**
   * Unique identifier for the event
   */
  id: string;

  /**
   * Type of the event
   */
  type: string;

  /**
   * Event data payload
   */
  data: Record<string, unknown>;

  /**
   * Optional metadata associated with the event
   */
  metadata?: Record<string, unknown>;
}

/**
 * EventBus client for managing subscriptions to an event store
 */
export class EventBus {
  private http: HttpClient;
  private eventStoreName: string;

  /**
   * Create a new EventBus client for a specific event store
   *
   * @param http - The HTTP client used for API requests
   * @param eventStoreName - The name of the event store to manage subscriptions for
   */
  constructor(http: HttpClient, eventStoreName: string) {
    this.http = http;
    this.eventStoreName = eventStoreName;
  }

  /**
   * Subscribe to events from this event store
   *
   * @param options - Configuration for the subscription
   * @returns The created subscription information
   * @throws {InvalidSubscriptionConfigError} When subscription configuration is invalid
   * @throws {EventStoreNotFoundError} When the event store doesn't exist
   * @throws {ValidationError} When request validation fails
   * @throws {AuthenticationError} When authentication fails
   *
   * @example
   * ```typescript
   * import {
   *   isInvalidSubscriptionConfigError,
   *   isEventStoreNotFoundError
   * } from '@delta-base/server';
   *
   * try {
   *   const subscription = await eventBus.subscribe({
   *     eventFilter: 'user.*',
   *     subscriber: {
   *       type: SubscriberType.Webhook,
   *       config: {
   *         url: 'https://example.com/webhook',
   *         headers: { 'X-API-Key': 'secret' },
   *         retryPolicy: {
   *           maxAttempts: 3,
   *           backoffMinutes: 5
   *         }
   *       }
   *     }
   *   });
   *
   *   // Check if this is an existing subscription
   *   if (subscription.isExistingSubscription) {
   *     console.log('Found existing subscription:', subscription.message);
   *   } else {
   *     console.log('Created new subscription:', subscription.message);
   *   }
   * } catch (error) {
   *   if (isInvalidSubscriptionConfigError(error)) {
   *     console.log(`Invalid configuration: ${error.configError}`);
   *     // Handle invalid subscription config
   *   } else if (isEventStoreNotFoundError(error)) {
   *     console.log(`Event store '${error.eventStoreId}' not found`);
   *     // Handle missing event store
   *   } else {
   *     throw error; // Re-throw unknown errors
   *   }
   * }
   * ```
   */
  async subscribe(options: SubscribeOptions): Promise<Subscription> {
    const response = await this.http.post<SubscriptionResponse>(
      `/api/event-stores/${this.eventStoreName}/subscribe`,
      options as unknown as Record<string, unknown>
    );

    return this.mapSubscriptionResponse(response);
  }

  /**
   * Create a webhook subscription
   *
   * @param eventFilter - Pattern determining which events to receive
   * @param url - The URL that will receive HTTP POST requests with events
   * @param options - Additional configuration options
   * @returns The created subscription information
   *
   * @example
   * ```typescript
   * // Subscribe to all user events
   * const subscription = await eventBus.subscribeWebhook(
   *   'user.*',
   *   'https://example.com/webhook',
   *   {
   *     headers: { 'X-API-Key': 'secret' },
   *     retryPolicy: {
   *       maxAttempts: 3,
   *       backoffMinutes: 5
   *     }
   *   }
   * );
   *
   * // Check if this is an existing subscription with the same configuration
   * if (subscription.isExistingSubscription) {
   *   console.log('Reusing existing subscription:', subscription.message);
   * } else {
   *   console.log('Created new subscription:', subscription.message);
   * }
   * ```
   */
  async subscribeWebhook(
    eventFilter: EventFilterPattern,
    url: string,
    options: Omit<WebhookConfig, 'url'> & {
      subscriberId?: string;
      initialPosition?: InitialPosition;
    } = {}
  ): Promise<Subscription> {
    const { subscriberId, initialPosition, ...webhookOptions } = options;

    return this.subscribe({
      eventFilter,
      subscriberId,
      initialPosition,
      subscriber: {
        type: SubscriberType.Webhook,
        config: {
          url,
          ...webhookOptions,
        },
      },
    });
  }

  // /**
  //  * Create a Durable Object subscription
  //  *
  //  * @param eventFilter - Pattern determining which events to receive
  //  * @param namespace - The Durable Object namespace binding name in your worker
  //  * @param id - The ID of the Durable Object instance to deliver events to
  //  * @param options - Additional configuration options
  //  * @returns The created subscription information
  //  *
  //  * @example
  //  * ```typescript
  //  * // Subscribe to order events with a Durable Object
  //  * const subscription = await eventBus.subscribeDurableObject(
  //  *   'order.*',
  //  *   'ORDER_PROCESSOR',
  //  *   'order-processor-instance-1',
  //  *   {
  //  *     retryPolicy: {
  //  *       maxAttempts: 5,
  //  *       backoffMinutes: 10
  //  *     }
  //  *   }
  //  * );
  //  * ```
  //  */
  // async subscribeDurableObject(
  //   eventFilter: EventFilterPattern,
  //   namespace: string,
  //   id: string,
  //   options: Omit<DurableObjectConfig, 'namespace' | 'id'> & {
  //     subscriberId?: string;
  //     initialPosition?: InitialPosition;
  //   } = {}
  // ): Promise<Subscription> {
  //   const { subscriberId, initialPosition, ...doOptions } = options;

  //   return this.subscribe({
  //     eventFilter,
  //     subscriberId,
  //     initialPosition,
  //     subscriber: {
  //       type: SubscriberType.DurableObject,
  //       config: {
  //         namespace,
  //         id,
  //         ...doOptions,
  //       },
  //     },
  //   });
  // }

  // /**
  //  * Create a queue subscription
  //  *
  //  * @param eventFilter - Pattern determining which events to receive
  //  * @param queueName - The name of the queue to send events to
  //  * @param options - Additional configuration options
  //  * @returns The created subscription information
  //  *
  //  * @example
  //  * ```typescript
  //  * // Subscribe to notification events with a queue
  //  * const subscription = await eventBus.subscribeQueue(
  //  *   'notification.*',
  //  *   'notification-queue',
  //  *   {
  //  *     region: 'us-east-1',
  //  *     batchSize: 10
  //  *   }
  //  * );
  //  * ```
  //  */
  // async subscribeQueue(
  //   eventFilter: EventFilterPattern,
  //   queueName: string,
  //   options: Omit<QueueConfig, 'queueName'> & {
  //     subscriberId?: string;
  //     initialPosition?: InitialPosition;
  //   } = {}
  // ): Promise<Subscription> {
  //   const { subscriberId, initialPosition, ...queueOptions } = options;

  //   return this.subscribe({
  //     eventFilter,
  //     subscriberId,
  //     initialPosition,
  //     subscriber: {
  //       type: SubscriberType.Queue,
  //       config: {
  //         queueName,
  //         ...queueOptions,
  //       },
  //     },
  //   });
  // }

  // /**
  //  * Create a Cloudflare Workflow subscription
  //  *
  //  * @param eventFilter - Pattern determining which events to receive
  //  * @param bindingName - The name of the workflow binding in your worker
  //  * @param options - Additional configuration options
  //  * @returns The created subscription information
  //  *
  //  * @example
  //  * ```typescript
  //  * // Subscribe to all events with a Cloudflare Workflow
  //  * const subscription = await eventBus.subscribeWorkflow(
  //  *   '*',
  //  *   'EVENT_WORKFLOW'
  //  * );
  //  * ```
  //  */
  // async subscribeWorkflow(
  //   eventFilter: EventFilterPattern,
  //   bindingName: string,
  //   options: Omit<CloudflareWorkflowConfig, 'bindingName'> & {
  //     subscriberId?: string;
  //     initialPosition?: InitialPosition;
  //   } = {}
  // ): Promise<Subscription> {
  //   const { subscriberId, initialPosition, ...workflowOptions } = options;

  //   return this.subscribe({
  //     eventFilter,
  //     subscriberId,
  //     initialPosition,
  //     subscriber: {
  //       type: SubscriberType.CloudflareWorkflow,
  //       config: {
  //         bindingName,
  //         ...workflowOptions,
  //       },
  //     },
  //   });
  // }

  // /**
  //  * Create a WebSocket subscription
  //  *
  //  * @param eventFilter - Pattern determining which events to receive
  //  * @param managerId - The ID of the WebSocket manager to broadcast events to
  //  * @param options - Additional configuration options
  //  * @returns The created subscription information
  //  *
  //  * @example
  //  * ```typescript
  //  * // Subscribe to notification events with WebSockets
  //  * const subscription = await eventBus.subscribeWebSocket(
  //  *   'notification.*',
  //  *   'notifications-websocket-manager'
  //  * );
  //  * ```
  //  */
  // async subscribeWebSocket(
  //   eventFilter: EventFilterPattern,
  //   managerId: string,
  //   options: Omit<WebSocketConfig, 'managerId'> & {
  //     subscriberId?: string;
  //     initialPosition?: InitialPosition;
  //   } = {}
  // ): Promise<Subscription> {
  //   const { subscriberId, initialPosition, ...wsOptions } = options;

  //   return this.subscribe({
  //     eventFilter,
  //     subscriberId,
  //     initialPosition,
  //     subscriber: {
  //       type: SubscriberType.WebSocket,
  //       config: {
  //         managerId,
  //         ...wsOptions,
  //       },
  //     },
  //   });
  // }

  /**
   * Get details about a specific subscription
   *
   * @param subscriptionId - ID of the subscription to retrieve
   * @returns Subscription details
   *
   * @example
   * ```typescript
   * const subscription = await eventBus.getSubscription('sub_123456');
   * console.log(subscription.status); // 'ACTIVE'
   * ```
   */
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    const response = await this.http.get<SubscriptionResponse>(
      `/api/event-stores/${this.eventStoreName}/subscriptions/${subscriptionId}`
    );

    return this.mapSubscriptionResponse(response);
  }

  /**
   * List all subscriptions for this event store
   *
   * @param options - Optional filtering and pagination parameters
   * @returns List of subscriptions and total count
   *
   * @example
   * ```typescript
   * // List all webhook subscriptions
   * const { subscriptions, totalCount } = await eventBus.listSubscriptions({
   *   subscriberType: SubscriberType.Webhook,
   *   limit: 20,
   *   offset: 0
   * });
   * ```
   */
  async listSubscriptions(
    options: ListSubscriptionsOptions = {}
  ): Promise<ListSubscriptionsResponse> {
    const params: Record<string, string> = {};

    if (options.subscriberType) {
      params.subscriberType = options.subscriberType;
    }

    if (options.eventFilter) {
      params.eventFilter = options.eventFilter;
    }

    if (options.limit) {
      params.limit = options.limit.toString();
    }

    if (options.offset) {
      params.offset = options.offset.toString();
    }

    const response = await this.http.get<{
      subscriptions: SubscriptionResponse[];
      totalCount: number;
    }>(`/api/event-stores/${this.eventStoreName}/subscriptions`, params);

    return {
      subscriptions: response.subscriptions.map((sub) =>
        this.mapSubscriptionResponse(sub)
      ),
      totalCount: response.totalCount,
    };
  }

  /**
   * Unsubscribe from events (delete a subscription)
   *
   * @param subscriptionId - ID of the subscription to delete
   * @returns Success message
   *
   * @example
   * ```typescript
   * const result = await eventBus.unsubscribe('sub_123456');
   * console.log(result.success); // true
   * ```
   */
  async unsubscribe(
    subscriptionId: string
  ): Promise<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(
      `/api/event-stores/${this.eventStoreName}/subscriptions/${subscriptionId}`
    );
  }

  /**
   * Map API subscription response to Subscription type
   *
   * @param response - The API response to map
   * @returns Mapped subscription
   * @private
   */
  private mapSubscriptionResponse(
    response: SubscriptionResponse
  ): Subscription {
    return {
      subscriptionId: response.subscriptionId,
      status: response.status as SubscriptionStatus,
      statusDetails: response.statusDetails,
      eventStoreId: response.eventStoreId,
      eventFilter: response.eventFilter,
      subscriberId: response.subscriberId,
      subscriberType: response.subscriberType as SubscriberType,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
      lastProcessedEventGlobalPosition:
        response.lastProcessedEventGlobalPosition,
      isExistingSubscription: response.isExistingSubscription,
      message: response.message,
    };
  }
}
